import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field, ValidationSummary, FieldNote } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { useIdentifies, useCreateIdentify, useUpdateIdentify, useDeleteIdentify } from "@/hooks/use-identifies";
import { useEndpoints } from "@/hooks/use-endpoints";
import type { Identify, IdentifyCreate } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const NAME_MAX_CHARS = 80;
const MATCH_MAX_CHARS = 255;
/** "[" / "]" open a section, ";" starts a comment, newlines split lines. */
const ILLEGAL_NAME_CHARS = /[[\];\r\n]/;
const ILLEGAL_VALUE_CHARS = /[;\r\n]/;

const defaultIdentify = {
  name: "",
  endpoint_id: null as string | null,
  match: null as string | null,
  match_header: null as string | null,
  match_request_uri: null as string | null,
  srv_lookups: true,
};

type IdentifyForm = typeof defaultIdentify;

export default function IdentifiesPage() {
  const { data, isLoading, isFetching, error, refetch } = useIdentifies();
  const { data: endpoints } = useEndpoints();
  const createMut = useCreateIdentify();
  const updateMut = useUpdateIdentify();
  const deleteMut = useDeleteIdentify();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Identify | null>(null);
  const [form, setForm] = useState<IdentifyForm>(defaultIdentify);
  const [showErrors, setShowErrors] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Identify | null>(null);

  const endpointNameById = useMemo(() => {
    const map = new Map<string, string>();
    endpoints?.forEach((ep) => map.set(ep.id, ep.name));
    return map;
  }, [endpoints]);

  const columns: Column<Identify>[] = useMemo(
    () => [
      { key: "name", header: "Name" },
      {
        key: "match",
        header: "Match",
        render: (i) => {
          const criteria = [i.match, i.match_header, i.match_request_uri].filter(Boolean) as string[];
          return criteria.length ? (
            <span className="font-mono text-xs">{criteria.join(" | ")}</span>
          ) : (
            <span className="text-xs text-destructive-text">no match criteria</span>
          );
        },
      },
      {
        key: "endpoint_id",
        header: "Endpoint",
        render: (i) => {
          if (!i.endpoint_id) return <span className="text-muted-foreground">unassigned</span>;
          const name = endpointNameById.get(i.endpoint_id);
          return name ? <span>{name}</span> : <span className="text-muted-foreground">unknown endpoint</span>;
        },
      },
      { key: "srv_lookups", header: "SRV", render: (i) => (i.srv_lookups ? "Yes" : "No") },
    ],
    [endpointNameById]
  );

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        (i.match ?? "").toLowerCase().includes(q)
    );
  }, [list, query]);

  const openCreate = () => { setEditing(null); setForm(defaultIdentify); setShowErrors(false); setOpen(true); };
  const openEdit = (i: Identify) => {
    setEditing(i);
    setForm({ name: i.name, endpoint_id: i.endpoint_id ?? null, match: i.match ?? null, match_header: i.match_header ?? null, match_request_uri: i.match_request_uri ?? null, srv_lookups: i.srv_lookups });
    setShowErrors(true);
    setOpen(true);
  };

  const errors = useMemo(() => {
    const e: Record<string, string> = {};

    const name = form.name.trim();
    if (!name) e.name = "Name is required.";
    else if (ILLEGAL_NAME_CHARS.test(name)) e.name = "Name cannot contain [ ] ; or line breaks.";
    else if (name.length > NAME_MAX_CHARS) e.name = `Name must be ${NAME_MAX_CHARS} characters or fewer.`;
    else if (list.some((i) => i.name === name && i.id !== editing?.id)) e.name = "An identify with this name already exists.";

    if (!form.endpoint_id) e.endpoint_id = "Endpoint is required — an identify without an endpoint produces an invalid pjsip.conf section.";

    const match = form.match?.trim() ?? "";
    const matchHeader = form.match_header?.trim() ?? "";
    const matchUri = form.match_request_uri?.trim() ?? "";

    if (!match && !matchHeader && !matchUri) {
      e.match = "Set at least one of Match IP / CIDR / Hostname, Match Header or Match Request URI — a rule with no criteria never matches.";
    }

    const lengthChecks: [string, string, string][] = [
      ["match", match, "Match IP / CIDR / Hostname"],
      ["match_header", matchHeader, "Match Header"],
      ["match_request_uri", matchUri, "Match Request URI"],
    ];
    for (const [key, value, label] of lengthChecks) {
      if (!value) continue;
      if (value.length > MATCH_MAX_CHARS) e[key] = `${label} must be ${MATCH_MAX_CHARS} characters or fewer.`;
      else if (ILLEGAL_VALUE_CHARS.test(value)) e[key] = `${label} cannot contain ; or line breaks.`;
    }

    if (!e.match && match && /\s/.test(match.replace(/\s*,\s*/g, ","))) {
      e.match = "Match accepts only IP addresses, CIDR ranges or hostnames (comma-separated). Remove the spaces — header matching belongs in Match Header.";
    }
    if (!e.match_header && matchHeader && !matchHeader.includes(":")) {
      e.match_header = "Match Header must be in the form Header-Name: value.";
    }

    const summary = Object.values(e);
    return { fields: e, summary, valid: summary.length === 0 };
  }, [form, list, editing]);

  const handleSave = () => {
    setShowErrors(true);
    if (!errors.valid) return;

    const payload: IdentifyCreate = {
      name: form.name.trim(),
      endpoint_id: form.endpoint_id,
      match: form.match?.trim() || null,
      match_header: form.match_header?.trim() || null,
      match_request_uri: form.match_request_uri?.trim() || null,
      srv_lookups: form.srv_lookups,
    };

    if (editing) {
      updateMut.mutate({ id: editing.id, data: payload }, { onSuccess: () => { setOpen(false); toast.success("Identify updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(payload, { onSuccess: () => { setOpen(false); toast.success("Identify created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const handleDelete = (i: Identify) => {
    deleteMut.mutate(i.id, { onSuccess: () => { toast.success("Identify deleted"); setPendingDelete(null); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
  };

  const updateField = (field: keyof IdentifyForm, value: unknown) => {
    setShowErrors(true);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const err = (key: string) => (showErrors ? errors.fields[key] : undefined);

  return (
    <div className="space-y-4">
      <PageHeader title="Identifies" description="PJSIP identify (IP-based endpoint matching)" />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search identifies..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={<Button onClick={openCreate} size="sm"><Plus size={14} /> Create Identify</Button>}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(i) => i.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No identifies configured."}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit Identify" : "Create Identify"}
        description="Match incoming SIP traffic to an endpoint by source IP or header."
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!errors.valid}
              loading={createMut.isPending || updateMut.isPending}
            >
              {editing ? "Update" : "Create"}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          <FormSection title="General" columns={2}>
            <Field
              label="Name"
              required
              htmlFor="name"
              error={err("name")}
              info="Required. Unique rule name, used as the pjsip.conf section name. Cannot contain [ ] ; or line breaks. Example: match-trunk-a, match-office-phones."
            >
              <Input id="name" value={form.name} error={!!err("name")} maxLength={NAME_MAX_CHARS} onChange={(e) => updateField("name", e.target.value)} />
            </Field>
            <Field
              label="Endpoint"
              required
              htmlFor="endpoint_id"
              error={err("endpoint_id")}
              info="Required (pjsip.conf 'endpoint'). The endpoint that incoming traffic matching this rule is attributed to. Without it Asterisk rejects the generated identify section, so the rule is not merely inactive — it is invalid."
            >
              <Select id="endpoint_id" value={form.endpoint_id ?? ""} error={!!err("endpoint_id")} onChange={(e) => updateField("endpoint_id", e.target.value || null)}>
                <option value="">Select endpoint</option>
                {endpoints?.map((ep) => <option key={ep.id} value={ep.id}>{ep.name}</option>)}
              </Select>
            </Field>
          </FormSection>

          <FormSection
            title="Matching"
            columns={1}
            description="At least one criterion is required. Leave the others empty if unused."
          >
            <Field
              label="Match IP / CIDR / Hostname"
              htmlFor="match"
              error={err("match")}
              info="Required unless another criterion is set (pjsip.conf 'match'). Accepts only an IP address, a CIDR range or a hostname — comma-separate several. Asterisk resolves hostnames at load time and matches the source IP of incoming requests. Examples: 203.0.113.50, 192.168.1.0/24, yourtrunk.pstn.twilio.com. Header matching is a separate field."
            >
              <Input id="match" value={form.match ?? ""} error={!!err("match")} onChange={(e) => updateField("match", e.target.value || null)} placeholder="192.168.0.0/16" />
            </Field>
            <Field
              label="Match Header"
              htmlFor="match_header"
              error={err("match_header")}
              info="Required unless another criterion is set (pjsip.conf 'match_header'). Matches a SIP header on the incoming request. Format: Header-Name: value. Example: X-Tenant: acme. Less common than IP matching; useful when several tenants share one source IP."
            >
              <Input id="match_header" value={form.match_header ?? ""} error={!!err("match_header")} onChange={(e) => updateField("match_header", e.target.value || null)} placeholder="X-Tenant: acme" />
            </Field>
            <Field
              label="Match Request URI"
              htmlFor="match_request_uri"
              error={err("match_request_uri")}
              info="Required unless another criterion is set (pjsip.conf 'match_request_uri'). Matches the Request-URI of the incoming INVITE. Useful for multi-tenant setups behind one IP. Example: sip:tenant-a.domain.com."
            >
              <Input id="match_request_uri" value={form.match_request_uri ?? ""} error={!!err("match_request_uri")} onChange={(e) => updateField("match_request_uri", e.target.value || null)} placeholder="sip:tenant-a.domain.com" />
            </Field>
            <FieldNote>
              All criteria set on one rule must match for the rule to apply. Use separate identifies for alternative sources.
            </FieldNote>
          </FormSection>

          <FormSection title="Options" columns={1} description="Optional.">
            <Switch id="srv_lookups" checked={form.srv_lookups} onChange={(e) => updateField("srv_lookups", e.target.checked)} label={<span>SRV Lookups <HelpIcon text="Optional (pjsip.conf 'srv_lookups'). On by default. When the Match field contains a hostname, Asterisk performs DNS SRV lookups on it and matches every resolved address. Has no effect when Match holds a plain IP or CIDR. Turn it off for static IP-only deployments or when the provider's SRV records list addresses you do not want to trust." /></span>} />
          </FormSection>

          <ValidationSummary errors={errors.summary} />
        </div>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && handleDelete(pendingDelete)}
        title="Delete identify"
        description={pendingDelete ? `Delete identify "${pendingDelete.name}"? This cannot be undone.` : undefined}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
